import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { posApi } from '@/services/api';
import type { POSCustomField } from '@/types';

interface CustomFieldsModalProps {
  onClose: () => void;
}

const emptyForm = {
  label: '',
  field_type: 'Input' as 'Input' | 'Select',
  options: '',
  is_unique: false,
  show_in_pos: false,
  is_active: true,
  sort_order: 0,
};

function getErrorMessage(error: unknown) {
  return (error as { response?: { data?: { message?: string } }; message?: string })
    ?.response?.data?.message || (error as Error)?.message || 'Could not save custom field';
}

export default function CustomFieldsModal({ onClose }: CustomFieldsModalProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<POSCustomField | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['customFields', 'all'],
    queryFn: async () => {
      const response = await posApi.getCustomFields(true);
      return response.data.message as POSCustomField[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => editing
      ? posApi.updateCustomField({ name: editing.fieldname, fieldname: editing.fieldname, ...form })
      : posApi.createCustomField(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] });
      setEditing(null);
      setForm(emptyForm);
      setError('');
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteCustomField(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['posItems'] });
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const startEdit = (field: POSCustomField) => {
    setEditing(field);
    setForm({
      label: field.label,
      field_type: field.field_type,
      options: field.options || '',
      is_unique: !!field.is_unique,
      show_in_pos: !!field.show_in_pos,
      is_active: !!field.is_active,
      sort_order: field.sort_order || 0,
    });
    setError('');
  };

  const reset = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">Custom Fields</h3>
            <p className="text-xs text-gray-500 mt-0.5">Fields appear on all Product and Service forms.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 grid md:grid-cols-2 gap-5">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError('');
              saveMutation.mutate();
            }}
          >
            <h4 className="text-sm font-semibold text-gray-900">{editing ? 'Edit field' : 'Add field'}</h4>
            {editing && <p className="text-xs text-gray-500">Key: {editing.fieldname}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <input
                required
                className="input-field"
                value={form.label}
                onChange={(event) => setForm({ ...form, label: event.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                className="input-field"
                value={form.field_type}
                onChange={(event) => setForm({ ...form, field_type: event.target.value as 'Input' | 'Select' })}
              >
                <option value="Input">Input</option>
                <option value="Select">Select</option>
              </select>
            </div>
            {form.field_type === 'Select' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                <textarea
                  required
                  rows={4}
                  className="input-field"
                  placeholder={'Red\nBlue\nGreen'}
                  value={form.options}
                  onChange={(event) => setForm({ ...form, options: event.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_unique}
                  onChange={(event) => setForm({ ...form, is_unique: event.target.checked })}
                />
                Values must be unique
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.show_in_pos}
                  onChange={(event) => setForm({ ...form, show_in_pos: event.target.checked })}
                />
                Show value on POS
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                />
                Active
              </label>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={saveMutation.isPending} className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> {editing ? 'Save' : 'Add Field'}
              </button>
              {editing && <button type="button" onClick={reset} className="btn-secondary">Cancel</button>}
            </div>
          </form>

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Configured fields</h4>
            {isLoading ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : fields.length === 0 ? (
              <p className="text-sm text-gray-400">No custom fields configured.</p>
            ) : (
              <div className="space-y-2">
                {fields.map((field) => (
                  <div key={field.fieldname} className="border border-gray-100 rounded-lg p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{field.label}</p>
                      <p className="text-xs text-gray-500">
                        {field.field_type}
                        {field.is_unique ? ' · Unique' : ''}
                        {field.show_in_pos ? ' · POS' : ''}
                        {!field.is_active ? ' · Inactive' : ''}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button type="button" onClick={() => startEdit(field)} className="p-1.5 text-gray-400 hover:text-primary-600">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete "${field.label}" and remove its values from all items?`)) {
                            deleteMutation.mutate(field.fieldname);
                          }
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
